import React, { useRef, useState, useEffect } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Box, Button, Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Link as RouterLink, Navigate } from 'react-router-dom';
import SEO from '../../shared/components/SEO';
import { BRAND_SANS_FONT } from '../../shared/design/theme';
import { LandingContainer, SectionTitle, SectionSubtitle } from '../home/HomePage.styles';
import '../home/HomePage.styles.css';
import { Footer } from '../../shared/components/Footer';
import { SiteHeader } from '../../shared/components/SiteHeader';
import { ACCENT, INK, DeckVisualPanel } from '../../shared/components/DeckVisuals';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import { FAQ_REPOSITORY } from '../../shared/services/faqChatService';
import { AUDIENCES } from './solutionsData';

// Self-contained scroll-reveal. These pages scroll INSIDE LandingContainer
// (not the window), so a capture-phase scroll + getBoundingClientRect check is
// used instead of IntersectionObserver (which is unreliable in that setup).
function useInView() {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let done = false;
    const check = () => {
      if (done) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      if (rect.top < vh - 40 && rect.bottom > 0) {
        done = true;
        setInView(true);
        window.removeEventListener('scroll', check, true);
        window.removeEventListener('resize', check);
      }
    };
    check();
    window.addEventListener('scroll', check, { passive: true, capture: true });
    window.addEventListener('resize', check);
    return () => {
      window.removeEventListener('scroll', check, true);
      window.removeEventListener('resize', check);
    };
  }, []);
  return [ref, inView];
}

// Quick and calm: a short fade with a small lift, no blur. Reveals that run
// long read as the page loading twice, so this is deliberately under half a
// second and fully disabled under prefers-reduced-motion.
const Reveal = ({ children, delay = 0, sx }) => {
  const [ref, inView] = useInView();
  return (
    <Box
      ref={ref}
      sx={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'none' : 'translateY(14px)',
        transition: `opacity 0.45s cubic-bezier(0.16,1,0.3,1) ${delay}s, transform 0.45s cubic-bezier(0.16,1,0.3,1) ${delay}s`,
        '@media (prefers-reduced-motion: reduce)': { opacity: 1, transform: 'none', transition: 'none' },
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

// The Microsoft marketplace listing: the one third-party credential we can
// point at honestly. Same URL and label as the landing hero so the proof
// signal reads identically wherever it appears.
const APPSOURCE_URL = 'https://marketplace.microsoft.com/en-us/product/WA200010426';

// The primary CTA label. Used in the hero and repeated verbatim at the close,
// and it matches the standing CTA in SiteHeader, so the whole page asks for one
// thing in one wording.
const PRIMARY_CTA_LABEL = 'See it on your own deck';

// How it works. The mechanic is the same for every audience, so this lives with
// the component rather than being triplicated per audience in solutionsData.
// Wording follows the `what-is-restack` and `how-works` FAQ answers.
const HOW_IT_WORKS = [
  {
    title: 'Open it in the file you are already in',
    body: 'Restack opens from the ribbon inside your live PowerPoint, Excel, and Word files. Nothing gets exported and nothing gets rebuilt somewhere else.',
  },
  {
    title: 'Say what you need in plain English',
    body: "Point it at the template, last cycle's file, and the new numbers, then describe the deliverable the way you would brief an associate.",
  },
  {
    title: 'It builds in the file, you steer',
    body: 'It researches, cross-checks, and writes into the live file on your template with every figure sourced, then keeps editing page by page as you refine it.',
  },
];

// One dedicated audience page (/solutions/<slug>): hero with the standing CTA
// and a proof panel, the problem-to-solution pair, the audience's workflows as
// icon-chip rows, how it works, the outcomes, the FAQ (which carries the 30-day
// risk reversal), and the closing CTA.
export default function AudiencePage({ slug }) {
  const audience = AUDIENCES[slug];
  const hair = alpha(INK, 0.08);

  const eyebrowSx = {
    fontFamily: BRAND_SANS_FONT,
    fontSize: '0.68rem',
    fontWeight: 600,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    color: ACCENT,
    mb: 1.5,
  };
  const bodySx = {
    fontFamily: BRAND_SANS_FONT,
    fontSize: '0.84rem',
    fontWeight: 400,
    lineHeight: 1.6,
    color: 'text.secondary',
  };
  // One section rhythm for the whole page: same top margin, same divider, same
  // padding above the content. Every band below uses it unmodified.
  const sectionSx = {
    mt: { xs: 5, md: 7 },
    pt: { xs: 4, md: 5 },
    borderTop: `1px solid ${hair}`,
  };

  if (!audience) return <Navigate to="/solutions" replace />;

  const faqs = (audience.faqIds || [])
    .map((id) => FAQ_REPOSITORY.find((entry) => entry.id === id))
    .filter(Boolean);

  // FAQPage structured data, built from the same entries the page renders so
  // answer engines quote what a reader actually sees.
  const schema = faqs.length
    ? [{
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map(({ question, answer }) => ({
        '@type': 'Question',
        name: question,
        acceptedAnswer: { '@type': 'Answer', text: answer },
      })),
    }]
    : null;

  return (
    <LandingContainer data-theme="light" sx={{ minHeight: '100vh' }}>
      <SEO
        title={audience.meta.title}
        description={audience.meta.description}
        canonical={`/solutions/${slug}`}
        schema={schema}
      />

      <SiteHeader />

      <Box
        component="main"
        id="main-content"
        sx={{
          maxWidth: 1100,
          mx: 'auto',
          px: { xs: 2.5, md: 4 },
          pt: { xs: 'calc(56px + 1.5rem)', md: 'calc(64px + 2.5rem)' },
          pb: { xs: 6, md: 9 },
        }}
      >
        {/* HERO */}
        <Box sx={{ textAlign: 'center', maxWidth: 820, mx: 'auto' }}>
          <Reveal>
            <Typography component="div" sx={eyebrowSx}>
              {audience.hero.eyebrow}
            </Typography>
            <SectionTitle component="h1" sx={{ mb: 2, textAlign: 'center' }}>
              {audience.hero.title}
            </SectionTitle>
            {/* Wider than the 600px SectionSubtitle default: these subtitles
                name several deliverables, and at 600px they stack 6+ lines. */}
            <SectionSubtitle sx={{ mx: 'auto', mb: 0, maxWidth: '100%' }}>
              {audience.hero.subtitle}
            </SectionSubtitle>
            {/* One primary CTA, plus the marketplace listing as the proof
                signal. Repeated verbatim at the close of the page. */}
            <Box
              sx={{
                mt: { xs: 3, md: 3.5 },
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 1.25,
              }}
            >
              <DemoCta label={PRIMARY_CTA_LABEL} />
              <AppSourceProof />
            </Box>
          </Reveal>
        </Box>

        {/* HERO VISUAL - the deliverable this audience ships, in the shared
            deck-visual language. Decorative: the page states the same thing in
            text above it, so it is hidden from assistive tech. */}
        <Reveal delay={0.08}>
          <Box aria-hidden="true" sx={{ mt: { xs: 4, md: 5 }, maxWidth: 760, mx: 'auto' }}>
            <DeckVisualPanel visual={audience.visual} variant="standalone" />
          </Box>
        </Reveal>

        {/* PROBLEM TO SOLUTION */}
        {audience.shift && (
        <Box component="section" sx={sectionSx}>
          <Reveal>
            <Box sx={{ textAlign: 'center', maxWidth: 720, mx: 'auto', mb: { xs: 3, md: 4 } }}>
              <Typography component="div" sx={eyebrowSx}>
                The rebuild
              </Typography>
              <SectionTitle
                component="h2"
                sx={{ textAlign: 'center', fontSize: { xs: '1.4rem', md: '1.9rem' }, mb: 0 }}
              >
                The deliverable is not the hard part. <em>Rebuilding it is.</em>
              </SectionTitle>
            </Box>
          </Reveal>
          <Reveal delay={0.06}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: { xs: 2, md: 2.5 },
              }}
            >
              <ShiftCard label="Every cycle today" body={audience.shift.before} />
              <ShiftCard label="With Restack" body={audience.shift.after} accent />
            </Box>
          </Reveal>
        </Box>
        )}

        {/* CASES - home-page "Why Restack" pattern: sticky header column on
            the left, the audience's workflows as divider-separated rows on
            the right. The left column reuses the per-audience `fold` copy. */}
        <Box component="section" sx={sectionSx}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '0.82fr 1.18fr' },
              gap: { xs: 4, md: 10 },
              alignItems: 'start',
            }}
          >
            {/* Left: section header, sticky on desktop. The sticky styles sit
                on the Reveal wrapper (the grid child) - putting them on an
                inner Box gives sticky no room to travel, since the wrapper is
                only as tall as its content. */}
            <Reveal sx={{ position: { md: 'sticky' }, top: { md: '120px' } }}>
              <Box>
                <Typography component="div" sx={eyebrowSx}>
                  {audience.fold.eyebrow}
                </Typography>
                <SectionTitle
                  component="h2"
                  sx={{
                    textAlign: 'left',
                    fontSize: { xs: '1.4rem', md: '1.9rem' },
                    fontWeight: 600,
                    lineHeight: 1.2,
                  }}
                >
                  The work your team <em>runs every cycle.</em>
                </SectionTitle>
                <SectionSubtitle sx={{ mx: 0, textAlign: 'left', fontSize: { xs: '0.9rem', md: '0.95rem' } }}>
                  {audience.fold.subtitle}
                </SectionSubtitle>
              </Box>
            </Reveal>

            {/* Right: the workflows as a single column of rows. */}
            <Box>
              {audience.cases.map((c, i, arr) => (
                <Reveal key={c.title} delay={Math.min(i * 0.05, 0.35)}>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: { xs: 2, md: 2.5 },
                      pt: i === 0 ? 0 : { xs: 2.5, md: 3 },
                      pb: i === arr.length - 1 ? 0 : { xs: 2.5, md: 3 },
                      borderBottom: i === arr.length - 1 ? 'none' : `1px solid ${hair}`,
                      '&:hover .uc-chip': { backgroundColor: alpha(ACCENT, 0.18), transform: 'scale(1.06)' },
                    }}
                  >
                    <Box
                      className="uc-chip"
                      aria-hidden="true"
                      sx={{
                        flexShrink: 0,
                        width: 44,
                        height: 44,
                        borderRadius: '12px',
                        backgroundColor: alpha(ACCENT, 0.1),
                        color: ACCENT,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'background-color 0.25s ease, transform 0.25s cubic-bezier(0.16,1,0.3,1)',
                        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                      }}
                    >
                      <c.Icon sx={{ fontSize: 22 }} />
                    </Box>
                    <Box>
                      <Typography
                        component="h3"
                        sx={{
                          fontFamily: BRAND_SANS_FONT,
                          fontWeight: 600,
                          fontSize: { xs: '1rem', md: '1.0625rem' },
                          color: 'text.primary',
                          mb: 0.75,
                        }}
                      >
                        {c.title}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: BRAND_SANS_FONT,
                          fontSize: { xs: '0.8125rem', md: '0.875rem' },
                          lineHeight: { xs: 1.5, md: 1.6 },
                          color: 'text.secondary',
                        }}
                      >
                        {c.body}
                      </Typography>
                    </Box>
                  </Box>
                </Reveal>
              ))}
            </Box>
          </Box>
        </Box>

        {/* HOW IT WORKS */}
        <Box component="section" sx={sectionSx}>
          <Reveal>
            <Box sx={{ textAlign: 'center', maxWidth: 640, mx: 'auto', mb: { xs: 3, md: 4 } }}>
              <Typography component="div" sx={eyebrowSx}>
                How it works
              </Typography>
              <SectionTitle
                component="h2"
                sx={{ textAlign: 'center', fontSize: { xs: '1.4rem', md: '1.9rem' }, mb: 0 }}
              >
                Three steps, <em>inside your own file.</em>
              </SectionTitle>
            </Box>
          </Reveal>
          <Reveal delay={0.06}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                gap: { xs: 2.5, md: 3 },
              }}
            >
              {HOW_IT_WORKS.map((step, i) => (
                <Box key={step.title}>
                  <Box
                    aria-hidden="true"
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: '50%',
                      backgroundColor: alpha(ACCENT, 0.1),
                      color: ACCENT,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontFamily: BRAND_SANS_FONT,
                      fontSize: '0.8rem',
                      fontWeight: 700,
                      mb: 1.5,
                    }}
                  >
                    {i + 1}
                  </Box>
                  <Typography
                    component="h3"
                    sx={{
                      fontFamily: BRAND_SANS_FONT,
                      fontWeight: 600,
                      fontSize: { xs: '1rem', md: '1.0625rem' },
                      color: 'text.primary',
                      mb: 0.75,
                    }}
                  >
                    {step.title}
                  </Typography>
                  <Typography sx={bodySx}>{step.body}</Typography>
                </Box>
              ))}
            </Box>
          </Reveal>
        </Box>

        {/* OUTCOMES */}
        {audience.outcomes?.length > 0 && (
        <Box component="section" sx={sectionSx}>
          <Reveal>
            <Box sx={{ textAlign: 'center', maxWidth: 640, mx: 'auto', mb: { xs: 3, md: 4 } }}>
              <Typography component="div" sx={eyebrowSx}>
                Outcomes
              </Typography>
              <SectionTitle
                component="h2"
                sx={{ textAlign: 'center', fontSize: { xs: '1.4rem', md: '1.9rem' }, mb: 0 }}
              >
                What changes for <em>the team.</em>
              </SectionTitle>
            </Box>
          </Reveal>
          <Reveal delay={0.06}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                gap: { xs: 2, md: 2.5 },
              }}
            >
              {audience.outcomes.map((item) => (
                <Box
                  key={item.title}
                  sx={{
                    p: { xs: 2.5, md: 3 },
                    borderRadius: '16px',
                    border: `1px solid ${hair}`,
                    backgroundColor: '#ffffff',
                    transition: 'border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
                    '&:hover': {
                      borderColor: alpha(ACCENT, 0.28),
                      boxShadow: `0 10px 28px ${alpha(INK, 0.07)}`,
                      transform: 'translateY(-2px)',
                    },
                    '@media (prefers-reduced-motion: reduce)': {
                      transition: 'none',
                      '&:hover': { transform: 'none' },
                    },
                  }}
                >
                  <Typography
                    component="h3"
                    sx={{
                      fontFamily: BRAND_SANS_FONT,
                      fontWeight: 600,
                      fontSize: { xs: '1rem', md: '1.0625rem' },
                      color: 'text.primary',
                      mb: 1,
                      letterSpacing: '-0.015em',
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography sx={bodySx}>{item.body}</Typography>
                </Box>
              ))}
            </Box>
          </Reveal>
        </Box>
        )}

        {/* FAQ - the objections a deal team raises before it will trial
            anything. Answers are pulled by id from FAQ_REPOSITORY, the same
            copy the support widget serves, and the list ends on the refund
            entry so the 30-day guarantee sits right above the closing CTA. */}
        {faqs.length > 0 && (
          <Box component="section" sx={sectionSx}>
            <Reveal>
              <Box sx={{ textAlign: 'center', maxWidth: 640, mx: 'auto', mb: { xs: 2.5, md: 3.5 } }}>
                <Typography component="div" sx={eyebrowSx}>
                  Questions
                </Typography>
                <SectionTitle
                  component="h2"
                  sx={{ textAlign: 'center', fontSize: { xs: '1.4rem', md: '1.9rem' }, mb: 0 }}
                >
                  Before you <em>trial it.</em>
                </SectionTitle>
              </Box>
            </Reveal>
            <Reveal delay={0.06}>
              <Box sx={{ maxWidth: 760, mx: 'auto' }}>
                {faqs.map(({ id, question, answer }) => (
                  <Accordion
                    key={id}
                    disableGutters
                    elevation={0}
                    square
                    sx={{
                      backgroundColor: 'transparent',
                      borderBottom: `1px solid ${hair}`,
                      '&::before': { display: 'none' },
                    }}
                  >
                    {/* component="span": MUI v7 already wraps the summary in a
                        heading, so a nested heading here would break the
                        document outline. */}
                    <AccordionSummary
                      expandIcon={<ExpandMoreRounded sx={{ color: alpha(INK, 0.45) }} />}
                      sx={{
                        px: 0.5,
                        minHeight: 52,
                        '& .MuiAccordionSummary-content': { my: 1 },
                        '&:focus-visible': {
                          outline: `2px solid ${ACCENT}`,
                          outlineOffset: '-2px',
                          borderRadius: '6px',
                        },
                      }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: BRAND_SANS_FONT,
                          fontSize: '0.92rem',
                          fontWeight: 600,
                          letterSpacing: '-0.01em',
                          color: 'text.primary',
                        }}
                      >
                        {question}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 0.5, pt: 0, pb: 2 }}>
                      <Typography sx={{ ...bodySx, fontSize: '0.86rem' }}>{answer}</Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            </Reveal>
          </Box>
        )}

        {/* CLOSING CTA - same ask, same wording as the hero. */}
        <Box
          component="section"
          sx={{ ...sectionSx, textAlign: 'center', maxWidth: 680, mx: 'auto' }}
        >
          <Reveal>
            <Typography
              component="h2"
              sx={{
                fontFamily: BRAND_SANS_FONT,
                fontSize: { xs: '1.05rem', md: '1.15rem' },
                fontWeight: 600,
                letterSpacing: '-0.015em',
                color: 'text.primary',
                mb: 1.25,
              }}
            >
              These are examples, not the limit.
            </Typography>
            <Typography sx={{ ...bodySx, maxWidth: 560, mx: 'auto', mb: 2.5 }}>
              If your workflow ends in a deck, model, or memo, Restack runs it on your templates. Tell us yours and we'll
              show you on a real file.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <DemoCta label={PRIMARY_CTA_LABEL} />
            </Box>
          </Reveal>
        </Box>
      </Box>

      <Footer />
    </LandingContainer>
  );
}

// One side of the problem-to-solution pair. The Restack side carries the rust
// treatment so the eye lands on the after state.
function ShiftCard({ label, body, accent = false }) {
  return (
    <Box
      sx={{
        p: { xs: 2.5, md: 3 },
        borderRadius: '16px',
        border: `1px solid ${accent ? alpha(ACCENT, 0.25) : alpha(INK, 0.08)}`,
        backgroundColor: accent ? alpha(ACCENT, 0.04) : alpha(INK, 0.02),
      }}
    >
      <Typography
        component="div"
        sx={{
          fontFamily: BRAND_SANS_FONT,
          fontSize: '0.68rem',
          fontWeight: 700,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: accent ? ACCENT : alpha(INK, 0.5),
          mb: 1,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: BRAND_SANS_FONT,
          fontSize: { xs: '0.875rem', md: '0.92rem' },
          lineHeight: 1.6,
          color: accent ? 'text.primary' : alpha(INK, 0.68),
        }}
      >
        {body}
      </Typography>
    </Box>
  );
}

// Demo-form CTA. Reuses the single founder-reachout form at /demo.
function DemoCta({ label }) {
  return (
    <Button
      component={RouterLink}
      to="/demo"
      endIcon={<ArrowForwardRounded sx={{ fontSize: '1rem' }} />}
      sx={{
        textTransform: 'none',
        fontFamily: BRAND_SANS_FONT,
        fontWeight: 600,
        fontSize: '0.85rem',
        letterSpacing: '-0.005em',
        borderRadius: '999px',
        px: 2.5,
        py: 0.9,
        color: '#FFFFFF',
        background: 'linear-gradient(135deg, #B83B1D 0%, #D4694E 100%)',
        boxShadow: '0 4px 14px rgba(184, 59, 29, 0.22)',
        transition: 'background 0.2s ease, box-shadow 0.2s ease, transform 0.12s ease',
        '&:hover': {
          background: 'linear-gradient(135deg, #A63419 0%, #C25A42 100%)',
          boxShadow: '0 6px 18px rgba(184, 59, 29, 0.3)',
        },
        '&:active': {
          transform: 'translateY(1px)',
          boxShadow: '0 2px 8px rgba(184, 59, 29, 0.26)',
        },
        // The rust fill swallows a rust focus ring, so the visible ring is ink.
        '&:focus-visible': {
          outline: `2px solid ${INK}`,
          outlineOffset: '3px',
        },
        '@media (prefers-reduced-motion: reduce)': {
          transition: 'none',
          '&:active': { transform: 'none' },
        },
      }}
    >
      {label}
    </Button>
  );
}

// Proof signal: the add-in is listed on the Microsoft marketplace. Secondary
// styling so it supports the primary CTA rather than competing with it.
function AppSourceProof() {
  return (
    <Box
      component="a"
      href={APPSOURCE_URL}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Live on Microsoft Store, opens in new tab"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        px: 2,
        py: 0.9,
        borderRadius: '999px',
        fontFamily: BRAND_SANS_FONT,
        fontSize: '0.85rem',
        fontWeight: 500,
        letterSpacing: '-0.005em',
        // Matches the MUI button line-height so the pill and the primary CTA
        // are the same height on the same row.
        lineHeight: 1.75,
        color: 'text.primary',
        backgroundColor: '#f4f1ed',
        border: `1px solid ${alpha(INK, 0.16)}`,
        textDecoration: 'none',
        transition: 'background-color 0.2s ease, border-color 0.2s ease, transform 0.12s ease',
        '&:hover': {
          backgroundColor: '#ede8e1',
          borderColor: alpha(INK, 0.28),
        },
        '&:active': { transform: 'translateY(1px)' },
        '&:focus-visible': {
          outline: `2px solid ${ACCENT}`,
          outlineOffset: '3px',
        },
        '@media (prefers-reduced-motion: reduce)': {
          transition: 'none',
          '&:active': { transform: 'none' },
        },
      }}
    >
      <svg width="15" height="15" viewBox="0 0 23 23" aria-hidden="true" style={{ flexShrink: 0 }}>
        <rect x="1" y="1" width="10" height="10" fill="#F25022" />
        <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
        <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
        <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
      </svg>
      Live on Microsoft Store
    </Box>
  );
}
